import type { InvoiceData } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const useGetInvoicesForPageEditor = ({
  sourceInvoiceId,
  excludeInvoiceIds,
  searchQuery,
}: {
  sourceInvoiceId?: string;
  excludeInvoiceIds: string[];
  searchQuery: string;
}) => {
  const { tenantId } = useTenant();

  const { data, loading, error, refetch } = useQuery<{
    invoicesForPageEditor: {
      invoices: {
        invoice: InvoiceData & { supplierName: string | undefined };
        matches: {
          supplier: boolean;
          invoiceNo: boolean;
        };
      }[];
    };
  }>(GET_INVOICES_FOR_PAGE_EDITOR_QUERY, {
    variables: { tenantId, sourceInvoiceId, excludeInvoiceIds, searchQuery },
  });

  return { data, loading, error, refetch };
};

const GET_INVOICES_FOR_PAGE_EDITOR_QUERY = gql`
  query GetInvoicesForPageEditor(
    $sourceInvoiceId: String
    $excludeInvoiceIds: [String!]!
    $searchQuery: String!
    $tenantId: String!
  ) {
    invoicesForPageEditor(
      sourceInvoiceId: $sourceInvoiceId
      excludeInvoiceIds: $excludeInvoiceIds
      searchQuery: $searchQuery
      tenantId: $tenantId
    ) {
      invoices {
        invoice {
          invoiceId
          invoiceNo
          invoiceText
          invoiceDate
          totalAmount
          currency
          status
          supplierName
          isDigitalInvoice
          fileName
        }
        matches {
          supplier
          invoiceNo
        }
      }
    }
  }
`;

export const useEditPages = () => {
  const { t } = useTranslation();
  const [editPages, { data, loading, error }] =
    useMutation(EDIT_PAGES_MUTATION);
  const client = useApolloClient();

  const handleEditPages = async (
    layout: {
      invoiceId: string;
      pageIndex: number;
    }[][],
  ) => {
    try {
      const response = await editPages({ variables: { layout } });

      toast.success(t("organize_documents.success"), {
        richColors: true,
        style: {
          padding: "16px",
          width: "356px",
        },
        duration: 6000,
        position: "top-center",
      });

      client.cache.evict({ fieldName: "invoices" });
      client.cache.evict({ fieldName: "fastlaneReviews" });
      client.cache.gc();

      return response;
    } catch (error) {
      toast.error(t("generic_error"), { richColors: true });
    }
  };

  return { editPages: handleEditPages, data, loading, error };
};

const EDIT_PAGES_MUTATION = gql`
  mutation InvoiceEditPages($layout: [[EditPagesPageInput!]!]!) {
    invoiceEditPages(layout: $layout) {
      success
    }
  }
`;
